import React, { useEffect, useMemo } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { FormElements } from "../../../shared/ui/forms/FormElements";
import HeadingH4 from "../../../shared/ui/headings/headingH4/HeadingH4";
import {
    formStructureMedicoRow,
    formStructurePrescrizioneRow,
    getValidationsTemplateGenPub
} from "./templateGenPubData";

import SecondaryButton from "../../../shared/ui/buttons/secondary-button/SecondaryButton";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import {
    selectIsPharmacy,
    selectSelectedCustomer
} from "../../../../redux/customer/customerSlice";
import { selectUser } from "../../../modules/Auth/_redux/authRedux";
import { toEnDateString } from "../../../utils/dateUtils";
import BtnPrimary from "../../../../shared/bootstrap/button/primary/BtnPrimary";
import BtnSecondary from "../../../../shared/bootstrap/button/secondary/BtnSecondary";
import { useLanguageForShared } from "../../../../shared/utils/languages/useLanguageForShared";
import BoxSearchPatients from "../templateGenFar/component/BoxSearchPatients";
import {
    formStructureContattiRow,
    formStructureDifferentDomicileRow, formStructureIndirizzoConsegnaRow, formStructureIndirizzoResidenceRow,
    formStructureNominativeRow
} from "../templateGenFar/templateGenFarData";
import Checkbox from "../../../../shared/bootstrap/form/checkbox/Checkbox";
import { IconSVG } from "../../../../shared/custom/icon/IconSVG";
import {
    formStructureEditPatientRegistryRow, formStructureEditPatientRegistryRowDomicile,
    formStructureEditPatientRegistryRowResidence
} from "../anagraficaPaziente/patientRegistryData";
import { useIsUpdatePatient } from "../useIsUpdatePatient";
import { updatePatient } from "../../../../redux/order/orderCrud";
import { setErrorTypeAction } from "../../../../redux/errors/errorsActions";
import HeadingH3 from "../../../shared/ui/headings/headingH3/HeadingH3";
import {
    getCustomerDistrictOptions,
    hasCustomerDistricts,
    isCustomerDistrictRequired,
    isDistrictGuidAllowed,
    sanitizePatientDistrictPayload
} from "../../../utils/districtUtils";

const ADDRESS_FIELDS = ["street", "number", "internal", "cap", "city", "province"];
const hasAddressValue = (address) =>
    ADDRESS_FIELDS.some((key) => {
        const value = address?.[key];
        return value !== undefined && value !== null && `${value}`.trim() !== "";
    });
const normalizeAddressValue = (value) =>
    value === undefined || value === null ? "" : `${value}`.trim();

// Helper to check if any residence/domicile field differs
function isDomicileDifferent(residence, domicile) {
    if (!residence || !domicile) return false;
    if (!hasAddressValue(domicile)) return false;

    return ADDRESS_FIELDS.some(
        key => normalizeAddressValue(residence[key]) !== normalizeAddressValue(domicile[key])
    );
}

const getPatientFormValue = (patient, isDifferent) => ({
    ...(patient || {}),
    domicile: isDifferent ? patient?.domicile : undefined,
});

const TemplateGenPub = ({ createOrder, order, goBack, patient }) => {
    const { t } = useLanguageForShared();

    const isPharmacy = useSelector(selectIsPharmacy);
    const selectedCustomer = useSelector(selectSelectedCustomer);

    const user = useSelector(selectUser);

    const dispatch = useDispatch();
    const districtOptions = getCustomerDistrictOptions(selectedCustomer);
    const validationsTemplateGenPub = useMemo(
        () => getValidationsTemplateGenPub(selectedCustomer),
        [selectedCustomer]
    );

    const initialIsDomicileDifferent = isDomicileDifferent(
        patient?.residence,
        patient?.domicile
    );
    const patientUpdateBaseline = useMemo(() => {
        if (!patient) return patient;

        const baselineIsDomicileDifferent = isDomicileDifferent(
            patient?.residence,
            patient?.domicile
        );
        return getPatientFormValue(patient, baselineIsDomicileDifferent);
    }, [patient]);

    const formik = useFormik({
        initialValues: {
            isDomicileDifferent: initialIsDomicileDifferent,
            prescription: {
                date: toEnDateString(new Date().getTime())
            },
            prescriber: {
                name: user.firstname,
                surname: user.lastname
            },
            patient: getPatientFormValue(patient, initialIsDomicileDifferent)
        },
        validationSchema: validationsTemplateGenPub,
        onSubmit: (values) => {
            values = {
                ...values,
                patient: {
                    ...((process.env.REACT_APP_ENABLE_DISTRICT !== "false") ? sanitizePatientDistrictPayload(selectedCustomer, values?.patient) : values?.patient)
                }
            };

            if (process.env.REACT_APP_ENABLE_DISTRICT !== "false") {
                const districtGuid = values?.patient?.residence?.districtGuid;
                if (isCustomerDistrictRequired(selectedCustomer) && !districtGuid) {
                    dispatch(setErrorTypeAction("Il distretto di residenza è obbligatorio"));
                    return;
                }

                if (hasCustomerDistricts(selectedCustomer) && !isDistrictGuidAllowed(selectedCustomer, districtGuid)) {
                    dispatch(setErrorTypeAction("Distretto di residenza non valido per il cliente selezionato"));
                    return;
                }
            }

            const applicant = {
                name: user.firstname,
                surname: user.lastname,
                data: toEnDateString(new Date().getTime())
            };

            const callback = (data = {}) => {
                createOrder({ ...values, applicant, ...data });
            };

            updateRegistry ? updatePatientAction(callback) : callback();
        }
    });

    const resetDomicileValidationState = React.useCallback(() => {
        formik.setTouched((prevTouched) => ({
            ...prevTouched,
            patient: {
                ...prevTouched?.patient,
                domicile: undefined,
            },
        }), false);
        formik.setErrors((prevErrors) => ({
            ...prevErrors,
            patient: {
                ...prevErrors?.patient,
                domicile: undefined,
            },
        }));
    }, [formik]);

    const domicileToggleFormik = {
        ...formik,
        values: {
            ...formik.values,
            isDomicileDifferent: formik.values.isDomicileDifferent ? ["true"] : []
        },
        setFieldValue: (name, value, shouldValidate) => {
            if (name !== "isDomicileDifferent") {
                return formik.setFieldValue(name, value, shouldValidate);
            }

            const checked = value.includes("true");
            formik.setFieldValue(name, checked, false);
            if (!checked) {
                formik.setFieldValue("patient.domicile", undefined, false);
                resetDomicileValidationState();
            }
        }
    };

    const updatePatientAction = (callback) => {
        updatePatient(sanitizePatientDistrictPayload(selectedCustomer, formik.values.patient)).then(
            (res) => {
                callback && callback({ patient: res });
            },
            (err) => {
                const message = err?.response?.data?.Message || err?.response?.data?.message;
                if (message === "ERR_CF_DUPLICATE") {
                    dispatch(setErrorTypeAction(t("sg.msg.cfAlreadyRegistered")));
                } else if (message === "ERR_CF_INCOHERENT") {
                    dispatch(setErrorTypeAction(t("sg.msg.cfWrong")));
                } else if (message === "ERR_EMAIL_DUPLICATE") {
                    dispatch(setErrorTypeAction(t("sg.msg.emailAlreadyRegistered")));
                } else if (message === "ERR_EMAIL_INCOHERENT") {
                    dispatch(setErrorTypeAction(t("sg.msg.emailWrong")));
                } else if (message === "ERR_DISTRICT_CUSTOMER_REQUIRED") {
                    dispatch(setErrorTypeAction("Se selezioni un distretto devi indicare il cliente"));
                } else if (message === "ERR_DISTRICT_CUSTOMER_INVALID") {
                    dispatch(setErrorTypeAction("Il distretto selezionato non appartiene al cliente"));
                } else {
                    dispatch(
                        setErrorTypeAction(t("sg.msg.errorUpdatePatient"))
                    );
                }
            }
        );
    };

    useEffect(() => {
        if (order) {
            const orderPatient = order.patient || patient;
            const nextIsDomicileDifferent = typeof order.isDomicileDifferent === "boolean"
                ? order.isDomicileDifferent
                : isDomicileDifferent(orderPatient?.residence, orderPatient?.domicile);
            formik.setValues({
                ...order,
                isDomicileDifferent: nextIsDomicileDifferent,
                patient: getPatientFormValue(orderPatient, nextIsDomicileDifferent),
                prescription: order.prescription || {
                    date: toEnDateString(new Date().getTime())
                },
                prescriber: order.prescriber || {
                    name: user.firstname,
                    surname: user.lastname
                }
            });
        }
    }, [order]);

    useEffect(() => {
        if (!order?.patient) {
            const nextIsDomicileDifferent = isDomicileDifferent(patient?.residence, patient?.domicile);
            formik.setFieldValue("patient", getPatientFormValue(patient, nextIsDomicileDifferent), false);
            formik.setFieldValue(
                "isDomicileDifferent",
                nextIsDomicileDifferent,
                false
            );
        }
    }, [patient]);
    const { updateRegistry, setUpdateRegistry, isUpdatePatient } = useIsUpdatePatient(
        patientUpdateBaseline,
        formik.values.patient
    );

    return (
        <div className={"templates-wrap template-gen-pub"}>
            <Form autocomplete="off" onSubmit={formik.handleSubmit}>
                {/*<HeadingH4>Richiedente</HeadingH4>*/}
                {/*<FormElements*/}
                {/*	structure={formStructureRichiedenteRow}*/}
                {/*	formik={formik}></FormElements>*/}
                <HeadingH3 className={"mb-3"}>{t("sg.infoPrescription")}</HeadingH3>

                <HeadingH4>{t("sg.order.prescriber.prescriberOrder")}</HeadingH4>

                <FormElements
                    structure={formStructureMedicoRow(t)}
                    formik={formik}></FormElements>
                <div className={"mt-3"}>
                    <HeadingH4>{t("sg.order.prescriber.prescriptionRight")}</HeadingH4>
                    <FormElements
                        structure={formStructurePrescrizioneRow(t)}
                        formik={formik}></FormElements>
                </div>

                {/*	<HeadingH4>Diagnosi</HeadingH4>
				<FormElements
					structure={formStructureDiagnosiRow}
					formik={formik}></FormElements>*/}
                <hr />
                <HeadingH3 className={"mt-3 mb-3"}>{t("sg.infoPatientPrescription")}</HeadingH3>

                <HeadingH4>{t("sg.patientLabel")}</HeadingH4>
                <FormElements
                    structure={
                        formStructureNominativeRow(t)
                    }
                    formik={formik}
                ></FormElements>

                <HeadingH4 className={"mt-3"}>{t("sg.contacts")}</HeadingH4>
                <FormElements
                    structure={formStructureContattiRow(t)}
                    formik={formik}
                ></FormElements>

                <HeadingH4 className={"mt-3"}>{t("sg.residenceAddress")}</HeadingH4>
                <FormElements
                    structure={formStructureIndirizzoResidenceRow(t, districtOptions)}
                    formik={formik}
                />

                <FormElements
                    structure={formStructureDifferentDomicileRow(t)}
                    formik={domicileToggleFormik}
                />

                <HeadingH4 className={"mt-3"}>{t("sg.homeAddress")}</HeadingH4>
                <FormElements
                    structure={formStructureIndirizzoConsegnaRow(t)}
                    formik={formik}
                    disabled={!formik.values.isDomicileDifferent}
                />

                {isUpdatePatient && <div className={"update-registry-check py-5 mb-5"}>
                    <Checkbox name="updateRegistry"
                        value={updateRegistry}
                        label={t("sg.updateRegistry")}
                        onChange={() => setUpdateRegistry(!updateRegistry)}
                        checked={updateRegistry} />
                    {updateRegistry && (
                        <div className="alert alert-warning mt-3 mb-0" role="alert">
                            {t("sg.updateRegistryProviderWarning")}
                        </div>
                    )}
                </div>}

                <Row className="justify-content-end" style={{ marginTop: "32px" }}>
                    <Col xs="auto">
                        <BtnSecondary onClick={goBack}>{isPharmacy ? t("action.cancel")
                            : t("action.back")}</BtnSecondary>
                    </Col>
                    <Col xs="auto">
                        <BtnPrimary type="submit">{t("action.next")}</BtnPrimary>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};

export default TemplateGenPub;
